import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import {
  MonetizationOn,
  CalendarMonth,
  CheckCircle,
  AccountBalanceWallet,
  CreditCard,
} from '@mui/icons-material';
import baseURL from '../../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-IN') : 'N/A';

const statusColors = {
  active: 'success',
  canceled: 'error',
  trialing: 'info',
  unpaid: 'warning',
  past_due: 'warning',
  incomplete: 'default',
};

const currencyOptions = [
  { code: 'INR', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'USD', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'AED', name: 'UAE Dirham', flag: '🇦🇪' },
];

const VesselOwnerSubscriptionDetails = () => {
  const { id: vesselOwnerId } = useParams();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTrialAlert, setShowTrialAlert] = useState(false);
  const [diffDays, setDiffDays] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [showPaymentOptionsDialog, setShowPaymentOptionsDialog] = useState(false);

  const [walletTopUpDialogOpen, setWalletTopUpDialogOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [daysToEnd, setDaysToEnd] = useState(null);         // for currentPeriodEnd
  const [daysLeft, setDaysLeft] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await axios.get(`${baseURL}/api/vesselowner-subscription/getSubscriptionDetails/${vesselOwnerId}`);
        const data = res.data;
        setSubscription(data);
        // console.log("data", data)

        // 🔹 Calculate days to currentPeriodEnd
        if (data.currentPeriodEnd || data.trialEnd) {
          const targetDate = data.currentPeriodEnd
            ? new Date(data.currentPeriodEnd)
            : new Date(data.trialEnd);

          const today = new Date();
          const timeDiff = targetDate.getTime() - today.getTime();
          const dayDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
          setDaysToEnd(dayDiff);
        }


        // 🔹 Calculate days to trialEnd
        if (data.trialEnd) {
          const trialEnd = new Date(data.trialEnd);
          const today = new Date();
          const diffTime = trialEnd.getTime() - today.getTime();
          const trialDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDiffDays(trialDays);

          if (data.status === 'trialing') {
            setShowTrialAlert(true);
          }
        }

      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch subscription data');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();

    const checktrail = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/vesselowner-subscription/checkTrialStatus/${vesselOwnerId}`);
        setDaysLeft(response.data);
      } catch (err) {
        console.log(err);
      }
    };

    checktrail();

  }, [vesselOwnerId]);
  ;
  // console.log("subscription", subscription)
  const payFromWallet = async () => {
    try {
      // console.log("subscription", subscription)
      const token = localStorage.getItem("token");
      await axios.post(
        `${baseURL}/api/vesselowner-subscription/subscribeUsingWallet`,
        {
          vesselOwnerId,
          vesselCount: subscription?.vesselCount,
          billingCycle: subscription?.billingCycle,
          stripeSubscriptionId: subscription?.stripeSubscriptionId
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Payment successful! Your subscription is now active.");
      setOpenDialog(false);
      // Add delay before reload so the toast is visible
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      const msg = error.response?.data?.message || 'Something went wrong during wallet payment';
      if (msg.toLowerCase().includes('wallet')) {
        toast.error(msg);
        setOpenDialog(false);
        setShowPaymentOptionsDialog(true);
      } else {
        toast.error(msg);
      }
    }
  };

  const handleTopUpSubmit = async () => {
    if (!topUpAmount || !selectedCurrency) {
      return toast.error('Please enter amount and select currency');
    }

    try {
      const response = await axios.post(`${baseURL}/api/wallet/topup`, {
        amount: topUpAmount,
        userId: vesselOwnerId,
        currency: selectedCurrency,

      });
      const clientSecret = response.data.clientSecret;
      setWalletTopUpDialogOpen(false);
      toast.success('Redirecting to payment gateway...');
      // navigate(`/VesselOwnerLayouts/${vesselOwnerId}/wallet-payment?secret=${clientSecret}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Top-up failed');
    }
  };

  const renewUsingWallet = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${baseURL}/api/vesselowner-subscription/renewUsingWallet`,
        {
          subscriptionId: subscription?._id,
          vesselOwnerId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("✅ Subscription renewed via wallet.");
      setOpenRenewDialog(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const msg = err.response?.data?.message || "Wallet renewal failed.";
      toast.error(msg);
      setOpenRenewDialog(false);
      setShowPaymentOptionsDialog(true); // fallback if wallet fails
    }
  };

  const handleCardRenewal = async () => {
    try {
      const res = await axios.post(
        `${baseURL}/api/vesselowner-subscription/addRenewal`,
        {
          subscriptionId: subscription?._id,
          email: subscription?.email,
        }
      );

      const { url } = res.data;
      if (url) {
        window.location.href = url; // redirect to Stripe checkout
      } else {
        toast.error("Stripe session not returned.");
      }
    } catch (err) {
      console.error("Stripe renewal error:", err);
      toast.error(err.response?.data?.message || "Stripe renewal failed.");
    }
  };


  if (loading) return <Box textAlign="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
  if (!subscription) return <Alert severity="info" sx={{ mt: 4 }}>No subscription data available.</Alert>;

  return (
    <Box maxWidth="md" mx="auto" mt={5}>
      <ToastContainer position="top-right" autoClose={4000} />

      {showTrialAlert && (
        <Alert severity="info" sx={{ mb: 3, display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight="bold">Welcome! You're currently on a free trial.</Typography>
          <Typography sx={{ mt: 1 }}>
            Your trial will expire in <strong>{diffDays} day{diffDays !== 1 ? 's' : ''}</strong>. Your free trial is active. Please activate a monthly or yearly plan to continue uninterrupted access.
          </Typography>
          <Button variant="contained" size="small" sx={{ mt: 2 }} onClick={() => setOpenDialog(true)}>
            Activate Now
          </Button>
        </Alert>
      )}

      {/* Payment Selection Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Payment Method</DialogTitle>
        <DialogContent dividers>
          <Typography>Select your preferred payment option:</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-around' }}>
          <Button variant="outlined" color="success" startIcon={<AccountBalanceWallet />} onClick={payFromWallet}>
            Pay from Wallet
          </Button>
          <Button variant="contained" color="primary" startIcon={<CreditCard />} onClick={() => {
            setOpenDialog(false);
            navigate(`/VesselOwnerLayouts/${vesselOwnerId}/stripsubscriptionvesselowner`);
          }}>
            Pay using Card
          </Button>
        </DialogActions>
      </Dialog>

      {/*Renewal Payment Selection Dialog */}
      <Dialog open={openRenewDialog} onClose={() => setOpenRenewDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Payment Method</DialogTitle>
        <DialogContent dividers>
          <Typography>Select your preferred payment option:</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-around' }}>
          <Button variant="outlined" color="success" startIcon={<AccountBalanceWallet />} onClick={renewUsingWallet}>
            Pay from Wallet
          </Button>

          <Button variant="contained" color="primary" startIcon={<CreditCard />} onClick={handleCardRenewal}>
            Pay using Card
          </Button>
        </DialogActions>
      </Dialog>


      {/* Wallet Not Found Dialog */}
      <Dialog open={showPaymentOptionsDialog} onClose={() => setShowPaymentOptionsDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Wallet Not Found or Insufficient Funds</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1 }}>
            Your wallet could not be found or has insufficient balance.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-around' }}>
          <Button variant="contained" color="secondary" onClick={() => {
            navigate(`/VesselOwnerLayouts/${vesselOwnerId}/wallet`);
          }}>
            Add Amount to Wallet
          </Button>
          <Button variant="contained" color="primary" startIcon={<CreditCard />} onClick={() => {
            setShowPaymentOptionsDialog(false);
            navigate(`/VesselOwnerLayouts/${vesselOwnerId}/stripsubscriptionvesselowner`);
          }}>
            Pay using Card
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wallet Top-Up Dialog */}
      <Dialog open={walletTopUpDialogOpen} onClose={() => setWalletTopUpDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Top-Up Wallet</DialogTitle>

        <DialogContent dividers>
          <FormControl fullWidth margin="normal">
            <InputLabel id="currency-label">Currency</InputLabel>
            <Select
              labelId="currency-label"
              value={selectedCurrency}
              label="Currency"
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              {currencyOptions.map((currency) => (
                <MenuItem key={currency.code} value={currency.code}>
                  {currency.flag} {currency.code} — {currency.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Amount"
            type="number"
            fullWidth
            margin="normal"
            value={topUpAmount}
            onChange={(e) => setTopUpAmount(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setWalletTopUpDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTopUpSubmit}>Proceed to Pay</Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Details */}
      <Paper elevation={6} sx={{ p: 4, borderRadius: 4, boxShadow: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom align="center" color='#e5e5e5ff' backgroundColor='#d24d19ff'>Subscription Details</Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><Typography><strong>Email:</strong> {subscription.email}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>Vessels Subscribed:</strong> {subscription.vesselCount}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography><strong>Billing Cycle:</strong> {subscription.billingCycle}</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Status:</strong>{' '}
              <Chip
                label={subscription.status}
                color={statusColors[subscription.status] || 'default'}
                size="small"
                icon={<CheckCircle fontSize="small" />}
              />
            </Typography>
          </Grid>
          {/* {console.log("subscription", subscription)} */}
          <Grid item xs={12} sm={6}>
            <Typography>
              {/* <MonetizationOn fontSize="small" sx={{ mr: 1 }} /> */}
              <strong>Amount:</strong> {subscription.priceInCents / 100} {subscription.currency}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <CalendarMonth fontSize="small" sx={{ mr: 1 }} />
              <strong>
                {subscription?.status === 'unpaid'
                  ? 'Updated Date:'
                  : subscription?.status === 'active'
                    ? 'Plan End Date:'
                    : 'Trial End Date:'}
              </strong>{" "}
              {subscription?.status === 'unpaid'
                ? formatDate(subscription.updatedAt)
                : subscription?.status === 'active' && subscription?.currentPeriodEnd
                  ? formatDate(subscription.currentPeriodEnd)
                  : formatDate(subscription?.trialEnd)}
            </Typography>

          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              onClick={() => setOpenConfirm(true)}
              sx={{
                backgroundColor: '#d24d19ff',
                color: '#fff',
                fontWeight: 'bold',
                textTransform: 'none',
                px: 3,
                py: 1,
                borderRadius: 2,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#6a9311ff',
                  boxShadow: 4
                }
              }}
            >
              Upgrade Plan
            </Button>

            {/* Confirm Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
              <DialogTitle>Confirm Plan Upgrade</DialogTitle>
              <DialogContent>
                <Typography>
                  If you upgrade the plan, your current plan will be stopped and the new plan will be activated.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenConfirm(false)} color="error">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setOpenConfirm(false);
                    navigate(`/VesselOwnerLayouts/${vesselOwnerId}/ModifySubscription`, {
                      state: {
                        subscriptionId: subscription?._id,
                        fetchvesselCount: subscription?.vesselCount
                      }
                    });
                  }}
                  sx={{ backgroundColor: "#6a1b9a" }}
                  variant="contained"
                >
                  Yes, Upgrade
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>

          {/* {console.log("daysToEnd", subscription)} */}
          {(daysToEnd !== null && daysToEnd <= 5) || subscription.status === 'unpaid' ? (
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                onClick={() => setOpenRenewDialog(true)}
                sx={{
                  backgroundColor: "#6a1b9a",   // Deep Purple
                  "&:hover": { backgroundColor: "#4a148c" }, // Darker Purple
                }}
              >
                Renew Subscription
              </Button>


            </Grid>
          ) : null}


        </Grid>
      </Paper>
    </Box>
  );
};

export default VesselOwnerSubscriptionDetails;
